import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import './ParametersWithBadges.css';

const ParametersWithBadges = ({ 
  processParameters = [],
  qualityAttributes = [],
  selectedProcessParameters = {},
  selectedQualityAttributes = {},
  onProcessParameterChange,
  onQualityAttributeChange,
  readOnlyMode = false,
  showUnified = false 
}) => {
  const [allParameters, setAllParameters] = useState([]);

  // Combine and normalize parameters
  useEffect(() => {
    const normalizedProcessParams = processParameters.map(param => ({
      ...param,
      category: 'process',
      badgeText: param.processParameterType === 'Input Process Parameter' ? 'Input' : 'Output',
      badgeClass: param.processParameterType === 'Input Process Parameter' ? 'tag-input' : 'tag-output',
      isSelected: selectedProcessParameters[param.name] || false
    }));

    const normalizedQualityAttributes = qualityAttributes.map(attr => ({
      ...attr,
      category: 'quality',
      badgeText: 'Output', // Quality attributes are always output
      badgeClass: 'tag-output',
      isSelected: selectedQualityAttributes[attr.name] || false
    }));

    if (showUnified) {
      // Combine all parameters for unified view
      setAllParameters([...normalizedProcessParams, ...normalizedQualityAttributes]);
    } else {
      // Keep them separate for tabbed view
      setAllParameters({
        process: normalizedProcessParams,
        quality: normalizedQualityAttributes
      });
    }
  }, [processParameters, qualityAttributes, selectedProcessParameters, selectedQualityAttributes, showUnified]);

  const handleParameterChange = (param, checked) => {
    if (param.category === 'process') {
      onProcessParameterChange && onProcessParameterChange(param.name, checked);
    } else if (param.category === 'quality') {
      onQualityAttributeChange && onQualityAttributeChange(param.name, checked);
    }
  };

  const ParameterItem = ({ param }) => (
    <div className="parameter-item-with-badge">
      <Form.Check
        type="checkbox"
        label={param.name}
        id={`param-${param.id || param.name.replace(/\s+/g, '_')}`}
        checked={param.isSelected}
        onChange={(e) => handleParameterChange(param, e.target.checked)}
        disabled={readOnlyMode}
        title={param.description || param.name}
        className="parameter-checkbox-with-badge"
      />
      <span className={`parameter-badge ${param.badgeClass}`}>
        {param.badgeText}
      </span>
    </div>
  );

  const ParameterGroup = ({ title, parameters, icon }) => (
    <div className="parameter-group">
      <div className="parameter-group-header">
        <h6>
          <span className="parameter-icon">{icon}</span>
          {title}
        </h6>
        <span className="parameter-count">{parameters.length}</span>
      </div>
      
      <div className="parameter-group-content">
        {parameters.length === 0 ? (
          <div className="empty-parameters-message">
            <p>No {title.toLowerCase()}</p>
          </div>
        ) : (
          parameters.map((param) => (
            <ParameterItem
              key={param.id || param.name}
              param={param}
            />
          ))
        )}
      </div>
    </div>
  );

  if (showUnified) {
    // Unified view - all parameters together
    return (
      <div className="parameters-with-badges-unified">
        <div className="section-header">
          <h5>Process Parameters & Quality Attributes</h5>
          <p className="section-description">
            Select the parameters and attributes you want to include in your analysis
          </p>
        </div>
        
        <div className="unified-parameters-grid">
          {allParameters.map((param) => (
            <ParameterItem
              key={`${param.category}-${param.id || param.name}`}
              param={param}
            />
          ))}
        </div>
        
        {allParameters.length === 0 && (
          <div className="no-parameters-message">
            <p>No parameters available</p>
            <small>Parameters will be loaded from the database</small>
          </div>
        )}
      </div>
    );
  }

  // Separate view - grouped by type
  return (
    <div className="parameters-with-badges-grouped">
      <div className="section-header">
        <h5>Process Parameters & Quality Attributes</h5>
        <p className="section-description">
          Parameters are categorized by input/output and type
        </p>
      </div>
      
      <div className="parameters-groups-grid">
        <ParameterGroup
          title="Input Process Parameters"
          parameters={allParameters.process?.filter(p => p.badgeText === 'Input') || []}
          icon="📥"
        />
        
        <ParameterGroup
          title="Output Process Parameters"
          parameters={allParameters.process?.filter(p => p.badgeText === 'Output') || []}
          icon="📤"
        />
        
        <ParameterGroup
          title="Quality Attributes"
          parameters={allParameters.quality || []}
          icon="✨"
        />
      </div>
    </div>
  );
};

export default ParametersWithBadges;
